import { ReactElement, useState, useEffect } from "react"
import { Button, Loading } from "@wfp/ui"
import moment from "moment"
import { formatDateTime, SurveyStatus } from "constants/constants"
import { SurveyMode, permissionsHH } from "constants/household"
import { useLazyGetProjectsQuery, useLazyGetFormsQuery } from "services/core/dataFetchFromMoDa"
import { useGetApiKeyNameQuery, useGetApiKeyListQuery } from "services/core/user"
import useCommon from "hooks/useCommon"
import { useUpdateHouseholdFrequencyMutation } from "services/household/survey"
import RenderRow from "components/Shared/Property/RenderRow"
import SurveyFormFetch from "pages/HouseHold/SurveyFormFetch"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import { FormFetchData, FormModaData, ProjectModaData, SurveyById } from "interfaces/dataInterfaces"
import { ISurveyDataLoader } from "interfaces/survey"
import SurveyFetchDetails from "./SurveyFetchDetails"

type Props = {
  survey?: SurveyById
  mode: string
  changeMode: () => void
}

const SurveyDataLoader = ({ survey, mode, changeMode }: Props): ReactElement | null => {
  const initialFormFetch = {
    apiKey: survey?.apiKey ?? 0,
    formId: survey?.formId ?? 0,
    projectId: survey?.projectId ?? 0,
    frequencyId: 0,
    endDate: survey?.endDate ?? "",
  }
  const { showSnackBar } = useCommon()
  const [surveyData, setSurveyData] = useState<ISurveyDataLoader | null>(null)
  const [isLoadingModa, setIsLoadingModa] = useState<boolean>(false)
  const [validation, setValidation] = useState<boolean>(true)
  const [formFetch, setFormFetch] = useState<FormFetchData>(initialFormFetch)
  const [fetchingOperationJobCron, setFetchingOperationJobCron] = useState<{ id: string; cron: string }[]>([])
  const [fetchingMode, setFetchingMode] = useState<number>(1)

  const [getProjects] = useLazyGetProjectsQuery()
  const [getForms] = useLazyGetFormsQuery()
  const { data: apiKeys } = useGetApiKeyListQuery(
    { dataProviderPlatformId: survey?.sourceId ?? 0 },
    { skip: !survey?.sourceId },
  )
  const { data: apiKeyData } = useGetApiKeyNameQuery(
    { dataProviderKeyId: survey?.apiKey ?? 0 },
    { skip: !survey?.apiKey },
  )
  const [updateHouseholdFrequency, { isLoading: isUpdating }] = useUpdateHouseholdFrequencyMutation()

  const [projects, setProjects] = useState<ProjectModaData[] | undefined>(undefined)
  const [forms, setForms] = useState<FormModaData[] | undefined>(undefined)

  const reset = (): void => {
    setFormFetch(initialFormFetch)
    changeMode()
  }

  const getAllForms = async (list: ProjectModaData[]): Promise<void> => {
    if (apiKeys?.length && list?.length) {
      try {
        const promises = apiKeys.map(item =>
          getForms(
            {
              apiKeyId: item.value,
              projectId: list.find(el => el.projectid === survey?.projectId)?.projectid ?? 0,
            },
            true,
          ).unwrap(),
        )
        const resultPromises = await Promise.allSettled(promises)
        const result = resultPromises
          .filter(promise => promise.status === "fulfilled")
          .map(item => (item as PromiseFulfilledResult<FormModaData[]>)?.value)
          .flat()
          .filter((item, index, self) => self.findIndex(value => value.formid === item.formid) === index)
        setForms(result)
      } catch (error) {
        setForms([])
        showSnackBar("An error occurred while processing your request", "error")
      }
    }
  }

  const getAllProjectsAndForms = async (): Promise<void> => {
    if (apiKeys?.length) {
      try {
        const promises = apiKeys.map(item => getProjects({ apiKeyId: item.value }, true).unwrap())
        const resultPromises = await Promise.allSettled(promises)
        const result = resultPromises
          .filter(promise => promise.status === "fulfilled")
          .map(item => (item as PromiseFulfilledResult<ProjectModaData[]>)?.value)
          .flat()
          .filter((item, index, self) => self.findIndex(value => value.projectid === item.projectid) === index)
        setProjects(result)
        await getAllForms(result)
      } catch (error) {
        setProjects([])
        showSnackBar("An error occurred while processing your request", "error")
      }
    }
  }

  const transformSurvey = (data: SurveyById): ISurveyDataLoader => {
    return {
      uploadedDate: data.surveyCreateDate ? moment(data.surveyCreateDate).format(formatDateTime) : "",
      uploadedBy: data.userName,
      report: data.surveyValidationReport?.split("\r\n"),
      source: data.source ?? "CSV",
      apiKey: apiKeyData?.name ?? "",
      project: projects?.find(item => item.projectid === data.projectId)?.name ?? `hidden (id: ${data.projectId})`,
      form: forms?.find(item => item.formid === data.formId)?.title ?? `hidden (id: ${data.formId})`,
      fetchingMode: !data.fetchingOperationJobCron?.length
        ? ""
        : data.fetchingOperationJobCron[0]?.cron?.includes("Manual")
        ? "Manual"
        : "Scheduled",
      frequency: data.fetchingOperationJobCron?.map(item => item.cron),
      endDate: data.endDate ? moment(data.endDate).format(formatDateTime) : "",
    }
  }

  const handleEdit = async (): Promise<void> => {
    if (!validation) {
      showSnackBar("Test fetching is required", "warning")
      return
    }
    if (!formFetch.apiKey || !formFetch.formId || !formFetch.projectId || (fetchingMode === 2 && !formFetch?.endDate)) {
      showSnackBar("Some fields are required", "warning")
      return
    }
    try {
      await updateHouseholdFrequency({
        ...formFetch,
        launchAndForget: fetchingMode === 1,
        surveyId: Number(survey?.surveyId),
      })
      showSnackBar("Survey fetching edited successfully")
      reset()
    } catch (error) {
      showSnackBar("Something went wrong, please try again", "error")
    }
  }

  useEffect(() => {
    if (survey) {
      setSurveyData(transformSurvey(survey))
    }
  }, [survey, projects, forms, apiKeyData])

  useEffect(() => {
    ;(async () => {
      if (apiKeys?.length) {
        setIsLoadingModa(true)
        await getAllProjectsAndForms()
        setIsLoadingModa(false)
      }
    })()
  }, [apiKeys])

  useEffect(() => {
    if (survey) {
      setFormFetch(initialFormFetch)
      setFetchingOperationJobCron(survey.fetchingOperationJobCron ?? [])
      const type =
        survey.fetchingOperationJobCron?.length && !survey.fetchingOperationJobCron[0]?.cron?.includes("Manual") ? 2 : 1
      setFetchingMode(type)
    }
  }, [survey])

  const RenderPropertyList = ({ label, values }: { label: string; values?: string[] }): ReactElement | null => {
    return (
      <tr className="h-8 border-b border-solid border-gray-200">
        <th className="text-start align-top font-bold pt-2">{label}:</th>
        <td className="px-4 pt-1 align-middle">
          <ul>
            {values?.length === 0 && <span className="text-gray-400">N/A</span>}
            {values?.map((item, index) => (
              <li key={`report-${index}`}>
                <p>{item}</p>
              </li>
            ))}
          </ul>
        </td>
      </tr>
    )
  }

  if (isLoadingModa) return <Loading />
  if (!surveyData) return null

  return (
    <div className="p-3">
      {mode === "view" && (
        <>
          <table>
            <RenderRow label="Uploaded by" value={surveyData?.uploadedBy} />
            <RenderRow label="Uploaded date" value={surveyData?.uploadedDate} />
            <RenderRow label="Source" value={surveyData?.source} />
            {survey?.isImportByFetch ? (
              <>
                <RenderRow label="API" value={surveyData?.apiKey} />
                <RenderRow label="Project" value={surveyData?.project} />
                <RenderRow label="Survey" value={surveyData?.form} />
                <RenderRow label="Fetching mode" value={surveyData?.fetchingMode} />
                <RenderPropertyList label="Frequency" values={surveyData?.frequency} />
                <RenderRow label="Fetch until" value={surveyData?.endDate} />
              </>
            ) : (
              <RenderPropertyList label="Report" values={surveyData?.report} />
            )}
          </table>

          {survey?.isImportByFetch && <SurveyFetchDetails surveyId={Number(survey?.surveyId)} />}
        </>
      )}

      {mode === "edit" && (
        <SurveyFormFetch
          data={formFetch}
          setData={setFormFetch}
          fetchingMode={fetchingMode}
          setFetchingMode={setFetchingMode}
          currentMode={SurveyMode.MODA}
          cron={fetchingOperationJobCron}
          currentApiKey={{ value: Number(apiKeyData?.dataProviderKeyId), name: apiKeyData?.name ?? "" }}
          editMode
          setValidation={setValidation}
        />
      )}

      {survey?.surveyStatusId === SurveyStatus.IN_PROGRESS && survey?.isImportByFetch && (
        <PermissionWrapper {...permissionsHH} feature="edit">
          <div className="flex justify-between mt-4">
            <Button onClick={reset} kind={mode === "edit" ? "tertiary" : "primary"} disabled={isUpdating}>
              {mode === "edit" ? "Cancel" : "Edit"}
            </Button>

            {mode === "edit" && (
              <Button onClick={handleEdit} disabled={isUpdating}>
                Save
              </Button>
            )}
          </div>
        </PermissionWrapper>
      )}
    </div>
  )
}

export default SurveyDataLoader
